// 6 service practices — DevOps/DevSecOps-first lineup
export const SERVICES = [
  {
    slug: "devsecops",
    code: "01",
    icon: "git-merge",
    title: "DevSecOps Engineering",
    tagline: "Secure-by-default pipelines from code commit to production.",
    overview:
      "We design, build, and operate DevSecOps pipelines that embed security at every stage of the software delivery lifecycle. From CI/CD automation with GitLab CI, Jenkins, and ArgoCD to container security scanning with Trivy and Snyk — we ensure your code is secure before it ships. Our pipelines are built for federal compliance: STIG-hardened, RMF-aligned, and audit-ready from day one.",
    capabilities: [
      { title: "CI/CD Pipeline Automation", desc: "GitLab CI, Jenkins, GitHub Actions, and ArgoCD — designed for repeatable, audit-ready delivery." },
      { title: "Container & Image Security", desc: "Trivy, Snyk, and Anchore image scanning integrated into the pipeline before workloads reach production." },
      { title: "STIG Automation & Hardening", desc: "Automated STIG application and drift detection across Linux, Windows, and container baselines." },
      { title: "Secrets Management", desc: "HashiCorp Vault, AWS Secrets Manager, and Azure Key Vault — secrets never touch the repo." },
      { title: "Policy as Code", desc: "OPA, Kyverno, and Sentinel guardrails enforced at admission and at runtime." },
      { title: "Continuous ATO (cATO)", desc: "Automated evidence collection and control monitoring integrated into the pipeline." },
    ],
    benefits: [
      "Deployment cycle time reduced from weeks to hours",
      "ATO 6+ months → under 4 weeks via continuous evidence",
      "Vulnerabilities caught pre-production, not in audit",
      "Pipeline artifacts double as audit deliverables",
    ],
    useCases: [
      { title: "DoD Mission CI/CD Modernization", desc: "Replace monthly release trains with STIG-compliant daily secure deploys." },
      { title: "Continuous ATO Program Build-Out", desc: "Automate evidence collection mapped to NIST 800-53 controls for ongoing authorization." },
      { title: "Enterprise GitOps Transformation", desc: "Move from ticket-driven ops to ArgoCD/Flux-managed declarative platforms." },
    ],
    image: "https://images.unsplash.com/photo-1722080826167-4ea87368cbc5?crop=entropy&cs=srgb&fm=jpg&w=1200",
  },
  {
    slug: "cloud",
    code: "02",
    icon: "cloud",
    title: "Cloud Engineering & Architecture",
    tagline: "Zero Trust cloud foundations engineered for federal mission and enterprise scale.",
    overview:
      "We architect secure, compliance-aligned cloud environments across AWS, Microsoft Azure, and Google Cloud. Our teams deliver IL5-ready landing zones, automated guardrails, and Terraform-codified infrastructure that reduces Authority to Operate (ATO) timelines from months to weeks. Whether it's a multi-account GovCloud topology or a hybrid cloud migration, we build it right the first time.",
    capabilities: [
      { title: "Multi-Cloud Architecture", desc: "AWS GovCloud, Azure Government, and GCP landing zones designed for mission workloads." },
      { title: "Infrastructure as Code", desc: "Terraform, Pulumi, and CloudFormation for auditable, reproducible provisioning." },
      { title: "Cloud Migration & Modernization", desc: "Rehost, replatform, refactor — based on workload economics and risk tolerance." },
      { title: "Landing Zone Design & Governance", desc: "Pre-hardened baselines mapped to FedRAMP, IL4/IL5, and corporate policy boundaries." },
      { title: "Cloud FinOps & Cost Optimization", desc: "Spend visibility, unit economics, and continuous rightsizing across accounts." },
      { title: "IL4–IL6 Environment Engineering", desc: "Hardened environments aligned to DoD CC SRG and Zero Trust reference architecture." },
    ],
    benefits: [
      "Accelerated ATO from months to weeks",
      "Predictable, governed cost envelope",
      "Portable workloads across GovCloud regions",
      "Provable compliance with continuous evidence",
    ],
    useCases: [
      { title: "Federal Civilian Cloud Modernization", desc: "Migrate legacy mission applications to a FedRAMP-authorized cloud foundation." },
      { title: "DoD Mission Systems IL4–IL5", desc: "Deploy hardened environments aligned to DoD CC SRG and Zero Trust." },
      { title: "Enterprise Hybrid Cloud Migration", desc: "Design a governed multi-account topology connecting on-prem to public cloud." },
    ],
    image: "https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
  },
  {
    slug: "platform",
    code: "03",
    icon: "boxes",
    title: "Platform Engineering & Kubernetes",
    tagline: "Container orchestration and platform services built for production at scale.",
    overview:
      "We build and operate Kubernetes-based platforms that give development teams self-service infrastructure with guardrails. From EKS/AKS/GKE to OpenShift, we handle cluster architecture, service mesh, observability, and GitOps workflows so your teams ship faster without sacrificing security or reliability.",
    capabilities: [
      { title: "Kubernetes Cluster Operations", desc: "EKS, AKS, GKE, and OpenShift — multi-cluster, multi-region, with disaster recovery patterns." },
      { title: "Service Mesh", desc: "Istio and Linkerd for mTLS, traffic policy, and east-west observability." },
      { title: "GitOps Workflows", desc: "ArgoCD and Flux for declarative, drift-free platform operations." },
      { title: "Observability & Monitoring", desc: "Prometheus, Grafana, ELK, and Datadog with SLO-backed alerts." },
      { title: "Container Runtime Security", desc: "Falco, Tetragon, and admission control to detect and block runtime threats." },
      { title: "Internal Developer Platform (IDP)", desc: "Backstage-style portals that turn weeks of onboarding into minutes." },
    ],
    benefits: [
      "Self-service infrastructure with built-in guardrails",
      "Faster developer velocity, fewer ops tickets",
      "Production-grade observability and SLOs",
      "Container security enforced by default",
    ],
    useCases: [
      { title: "Internal Developer Platform Build-Out", desc: "Stand up Backstage with golden paths for every team's most common workloads." },
      { title: "Multi-Cluster Kubernetes Operations", desc: "Run hardened K8s across regions with consistent policy and observability." },
      { title: "GitOps Platform Modernization", desc: "Replace imperative ops with ArgoCD-managed declarative platforms." },
    ],
    image: "https://images.pexels.com/photos/4597280/pexels-photo-4597280.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
  },
  {
    slug: "cybersecurity",
    code: "04",
    icon: "shield",
    title: "Cybersecurity & Compliance",
    tagline: "NIST-aligned defense programs, continuous monitoring, and Zero Trust execution.",
    overview:
      "Our cybersecurity practice engineers and operates defense-grade security programs aligned to NIST 800-53, NIST 800-171, CMMC 2.0, RMF, and the DoD Zero Trust Reference Architecture. We deliver continuous compliance — not point-in-time audits. From vulnerability management to Zero Trust architecture implementation, we secure the mission.",
    capabilities: [
      { title: "Zero Trust Architecture", desc: "Identity, network, data, and workload pillars implemented against the DoD Reference Architecture." },
      { title: "Continuous ATO (cATO)", desc: "Automated evidence collection and control monitoring for ongoing authorization." },
      { title: "Vulnerability Management", desc: "Continuous scan, prioritize, and remediate workflows with SLA-backed closure." },
      { title: "Governance, Risk & Compliance", desc: "FedRAMP, CMMC, FISMA, HIPAA, and ISO 27001 program build-out." },
      { title: "SIEM & Security Operations", desc: "Splunk, ELK, and AWS Security Hub with detection engineering and playbooks." },
      { title: "Penetration Testing & Red Team", desc: "Adversary emulation and assumed-breach exercises against your real defenses." },
    ],
    benefits: [
      "Continuous, evidence-backed security posture",
      "ATO 6+ months → under 4 weeks",
      "Defense-in-depth across identity, network, and data",
      "Auditor-ready artifacts at any time",
    ],
    useCases: [
      { title: "CMMC 2.0 Level 2 Readiness", desc: "Gap assessment, remediation, and certification support for the defense industrial base." },
      { title: "FedRAMP Authorization Support", desc: "End-to-end sponsorship including SSP authoring, 3PAO coordination, and ConMon." },
      { title: "Enterprise Zero Trust Rollout", desc: "Identity-first segmentation rollout aligned to NIST 800-207." },
    ],
    image: "https://images.unsplash.com/photo-1573489176014-6d0176081570?crop=entropy&cs=srgb&fm=jpg&w=1200",
  },
  {
    slug: "aiml",
    code: "05",
    icon: "brain",
    title: "AI/ML & Intelligent Automation",
    tagline: "Transform data into intelligent insights and automated decision-making.",
    overview:
      "We help organizations leverage artificial intelligence and machine learning to automate workflows, surface actionable insights, and build intelligent systems. From data pipeline engineering to ML model deployment on Kubernetes, we bridge the gap between data science and production operations.",
    capabilities: [
      { title: "Data Pipeline Engineering", desc: "Apache Airflow, Spark, and dbt for reliable, observable data movement." },
      { title: "ML Model Deployment & MLOps", desc: "Production-grade model serving with versioning, monitoring, and rollback." },
      { title: "Intelligent Process Automation", desc: "Combine RPA with ML to eliminate operational toil at scale." },
      { title: "NLP & Document Intelligence", desc: "Extract structure from contracts, FOIA requests, and regulatory filings." },
      { title: "AI-Powered Security Analytics", desc: "ML-driven detection on SIEM telemetry — surface real threats faster." },
      { title: "Responsible AI & Model Governance", desc: "Auditable model registries and bias/drift monitoring for regulated workloads." },
    ],
    benefits: [
      "Data converted to decisions, not just dashboards",
      "Production-grade ML deployments — not POCs",
      "Auditable AI workflows for regulated environments",
      "Lower operational toil through intelligent automation",
    ],
    useCases: [
      { title: "Document Intelligence for Federal Agencies", desc: "Automated extraction across FOIA, acquisition, and case files." },
      { title: "AI-Powered Security Analytics", desc: "Detect adversary behavior patterns invisible to signature-based tools." },
      { title: "ML Platform on Kubernetes", desc: "Stand up Kubeflow / SageMaker-on-EKS for self-serve model deployment." },
    ],
    image: "https://images.pexels.com/photos/586056/pexels-photo-586056.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
  },
  {
    slug: "pmo",
    code: "06",
    icon: "briefcase",
    title: "Project & Program Management",
    tagline: "Disciplined execution for complex federal and enterprise initiatives.",
    overview:
      "We deliver structured project and program management for complex technology initiatives — managing scope, risk, schedule, and stakeholder alignment across multi-team, multi-vendor environments. Our PMs understand the tech, not just the Gantt chart.",
    capabilities: [
      { title: "Agile / SAFe Program Management", desc: "Scaled agile delivery across release trains, dependencies, and PI planning." },
      { title: "Federal Acquisition & Contract Support", desc: "Capture support, proposal development, and post-award program execution." },
      { title: "Technical Program Management", desc: "PMs who can read the architecture diagram, not just the schedule." },
      { title: "Risk & Issue Management", desc: "Quantitative risk modeling and structured escalation paths." },
      { title: "Stakeholder Communication", desc: "Executive-grade reporting that translates engineering reality into business decisions." },
      { title: "Multi-Vendor Orchestration", desc: "Keep prime, sub, and partner teams aligned to mission outcomes." },
    ],
    benefits: [
      "Predictable delivery on complex initiatives",
      "Technical depth — not just scheduling",
      "Stakeholder alignment across vendors and agencies",
      "Risk surfaced early, not at delivery",
    ],
    useCases: [
      { title: "Federal Cloud Migration Program", desc: "Multi-year, multi-vendor migration with embedded compliance milestones." },
      { title: "Multi-Vendor DevSecOps Rollout", desc: "Coordinate prime contractor, subs, and platform teams against one delivery plan." },
      { title: "Agile / SAFe Enterprise Transformation", desc: "Stand up release trains, dependency boards, and metrics that survive audit." },
    ],
    image: "https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
export const getNextService = (slug) => {
  const i = SERVICES.findIndex((s) => s.slug === slug);
  return SERVICES[(i + 1) % SERVICES.length];
};
